import React, { useState, useEffect, useCallback, useMemo } from "react";
import DatePicker from "react-datepicker";
import { useSearchParams } from "react-router-dom";
import "react-datepicker/dist/react-datepicker.css";
import { toast } from "react-toastify";
import ApiService from "../../../services/ApiService";
import Loader from "../../../components/Loader";
import BookingDetailsOffcanvas from "../../../components/BookingDetailsOffcanvas";
import ManualBookingOffcanvas from "../../../components/ManualBookingOffcanvas";
import { hasPermission } from "../../../utils/permissions";
import { todayInUAEDate } from "../../../utils/timezone";
import { formatSlotRange } from "../../../utils/helper";
import "./Calendar.css";

const toYMD = (date) => {
  const d = new Date(date);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const monthKey = (date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;

// Today at midnight, UAE time — used both as the calendar's initial selection
// and for the "can't book a past date" guard below.
const startOfToday = () => todayInUAEDate();

const BookingCalendarPage = () => {
  const [rooms, setRooms] = useState([]);
  const [roomFilter, setRoomFilter] = useState("all"); // calendar heatmap room scope
  const [visibleMonth, setVisibleMonth] = useState(startOfToday);
  const [days, setDays] = useState({}); // { 'YYYY-MM-DD': {status, total_slots, booked_slots} }
  const [summaryLoading, setSummaryLoading] = useState(false);

  // Today is selected by default, so its slots load immediately rather than
  // waiting for the admin to click a date.
  const [selectedDate, setSelectedDate] = useState(startOfToday); // Date object
  const [daySlots, setDaySlots] = useState(null); // { date, rooms: [{room, slots}] }
  const [slotsLoading, setSlotsLoading] = useState(false);

  const [manualPrefill, setManualPrefill] = useState(null);
  const [showManual, setShowManual] = useState(false);

  const [selectedBookingId, setSelectedBookingId] = useState(null);
  const [showBookingDetails, setShowBookingDetails] = useState(false);

  const [showBlockModal, setShowBlockModal] = useState(false);
  const [blockRangeStart, setBlockRangeStart] = useState("");
  const [blockRangeEnd, setBlockRangeEnd] = useState("");
  const [blockRangeReason, setBlockRangeReason] = useState("");
  const [blockRangeSubmitting, setBlockRangeSubmitting] = useState(false);

  const canManualCreate = hasPermission("bookings.manual-create");
  const canManageBlocks = hasPermission("bookings.block-dates");

  /* ── Rooms ── */
  useEffect(() => {
    (async () => {
      try {
        const res = await ApiService.request({ method: "GET", url: "admin/rooms-list" });
        if (res.data.status) setRooms(res.data.data || []);
      } catch {
        // handled globally by ApiService
      }
    })();
  }, []);

  /* ── Month summary (calendar heatmap) ── */
  const loadSummary = useCallback(async (month, room_id) => {
    try {
      setSummaryLoading(true);
      const params = new URLSearchParams({ month: monthKey(month) });
      if (room_id && room_id !== "all") params.append("room_id", room_id);
      const res = await ApiService.request({ method: "GET", url: `admin/bookings/calendar-summary?${params.toString()}` });
      if (res.data.status) setDays(res.data.data?.days || {});
      else toast.error(res.data.message);
    } catch {
      toast.error("Failed to load calendar");
    } finally {
      setSummaryLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSummary(visibleMonth, roomFilter);
  }, [visibleMonth, roomFilter, loadSummary]);

  /* ── Day slots ── */
  const loadDaySlots = useCallback(async (date) => {
    try {
      setSlotsLoading(true);
      const res = await ApiService.request({
        method: "GET",
        url: `admin/bookings/day-slots?date=${toYMD(date)}`,
      });
      if (res.data.status) setDaySlots(res.data.data);
      else {
        setDaySlots(null);
        toast.error(res.data.message);
      }
    } catch {
      toast.error("Failed to load slots for this date");
    } finally {
      setSlotsLoading(false);
    }
  }, []);

  const handleSelectDate = (date) => {
    setSelectedDate(date);
    loadDaySlots(date);
  };

  // selectedDate starts pre-filled with today, so its slots need loading once
  // up front — every later change goes through handleSelectDate instead.
  useEffect(() => {
    loadDaySlots(selectedDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleMonthChange = (date) => setVisibleMonth(date);

  const refreshAfterChange = () => {
    loadSummary(visibleMonth, roomFilter);
    if (selectedDate) loadDaySlots(selectedDate);
  };

  /* ── Blocked dates ── */
  // `prefillDate` seeds the start date — the selected calendar day when opened
  // from the "Block This Date" quick action, otherwise today.
  const openBlockModal = (prefillDate) => {
    const start = prefillDate ? toYMD(prefillDate) : (selectedDate ? toYMD(selectedDate) : toYMD(startOfToday()));
    setBlockRangeStart(start);
    setBlockRangeEnd("");
    setBlockRangeReason("");
    setShowBlockModal(true);
  };

  const handleBlockRangeSubmit = async () => {
    if (!blockRangeStart) { toast.error("Start date is required"); return; }
    if (blockRangeEnd && blockRangeEnd < blockRangeStart) { toast.error("End date cannot be before start date"); return; }

    setBlockRangeSubmitting(true);
    try {
      const res = await ApiService.request({
        method: "POST",
        url: "admin/blocked-dates",
        data: {
          start_date: blockRangeStart,
          end_date: blockRangeEnd || blockRangeStart,
          reason: blockRangeReason.trim() || undefined,
        },
      });
      if (res.data.status) {
        toast.success(res.data.message || "Blocked successfully!");
        setShowBlockModal(false);
        refreshAfterChange();
      } else {
        toast.error(res.data.message);
      }
    } catch {
      // handled globally
    } finally {
      setBlockRangeSubmitting(false);
    }
  };

  const handleUnblock = async (id) => {
    try {
      const res = await ApiService.request({ method: "DELETE", url: `admin/blocked-dates/${id}` });
      if (res.data.status) {
        toast.success(res.data.message || "Date unblocked successfully!");
        refreshAfterChange();
      } else {
        toast.error(res.data.message);
      }
    } catch {
      // handled globally
    }
  };

  /* ── Slot click handlers ── */
  const openSlotBooking = (room, slot) => {
    if (!canManualCreate) return;
    if (selectedDate < today) {
      toast.error("Cannot create a booking for a past date.");
      return;
    }
    setManualPrefill({
      room_id: room._id,
      room_title: room.title,
      is_icebox_room: room.is_icebox_room,
      date: toYMD(selectedDate),
      start_time: slot.start_time,
      end_time: slot.end_time,
      is_iced_slot: slot.is_iced_slot,
    });
    setShowManual(true);
  };

  const openBookedSlot = (slot) => {
    setSelectedBookingId(slot.booking._id);
    setShowBookingDetails(true);
  };

  const openBlankManualBooking = () => {
    setManualPrefill(null);
    setShowManual(true);
  };

  // `?new=1` (the app's "+" button) opens a blank manual booking straight
  // away; the param is dropped so a refresh or back doesn't reopen it.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (searchParams.get("new") !== "1") return;
    if (canManualCreate) openBlankManualBooking();
    setSearchParams({}, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  /* ── Calendar cell rendering ── */
  const dayStatus = useCallback((date) => days[toYMD(date)]?.status, [days]);

  const dayClassName = useCallback((date) => {
    const status = dayStatus(date);
    if (!status || status === "closed") return "bc-day bc-day-closed";
    return `bc-day bc-day-${status}`;
  }, [dayStatus]);

  const today = useMemo(() => startOfToday(), []);

  return (
    <>
      <div className="booking-calendar-page">
        <div className="bookings-page-header">
          <div>
            <h1 className="bookings-title">Booking Calendar</h1>
            <div className="bookings-breadcrumb">Dashboard <i className="bi bi-chevron-right"></i> Booking Calendar</div>
          </div>
          {(canManageBlocks || canManualCreate) && (
            <div className="bookings-header-right">
              {canManageBlocks && (
                <button className="btn btn-light bc-block-dates-header-btn" onClick={() => openBlockModal(null)}>
                  <i className="bi bi-slash-circle me-2"></i>Block Dates
                </button>
              )}
              {canManualCreate && (
                <button className="btn export-btn" onClick={openBlankManualBooking}>
                  <i className="bi bi-plus-lg me-2"></i>New Manual Booking
                </button>
              )}
            </div>
          )}
        </div>

        <div className="bc-layout">
          {/* Calendar card */}
          <div className="bc-calendar-card">
            <div className="bc-room-tabs">
              <button
                className={`bc-room-tab ${roomFilter === "all" ? "active" : ""}`}
                onClick={() => setRoomFilter("all")}
              >
                All Rooms
              </button>
              {rooms.map((r) => (
                <button
                  key={r._id}
                  className={`bc-room-tab ${roomFilter === r._id ? "active" : ""}`}
                  onClick={() => setRoomFilter(r._id)}
                >
                  {r.is_icebox_room && <i className="bi bi-snow2 me-1"></i>}
                  {r.title}
                </button>
              ))}
            </div>

            <div className="bc-datepicker-wrap">
              {summaryLoading && <div className="bc-summary-loading"><Loader text="Loading" size="20" /></div>}
              <DatePicker
                inline
                selected={selectedDate}
                onChange={handleSelectDate}
                onMonthChange={handleMonthChange}
                onYearChange={handleMonthChange}
                dayClassName={dayClassName}
                highlightDates={[today]}
              />
            </div>

            <div className="bc-legend">
              <span className="bc-legend-item"><i className="bc-dot bc-dot-available"></i>Available</span>
              <span className="bc-legend-item"><i className="bc-dot bc-dot-partial"></i>Partially Booked</span>
              <span className="bc-legend-item"><i className="bc-dot bc-dot-full"></i>Fully Booked</span>
              <span className="bc-legend-item"><i className="bc-dot bc-dot-closed"></i>Closed</span>
              <span className="bc-legend-item"><i className="bc-dot bc-dot-blocked"></i>Blocked</span>
            </div>
          </div>

          {/* Slots panel */}
          <div className="bc-slots-card">
            {!selectedDate && (
              <div className="bc-empty">
                <i className="bi bi-calendar-week"></i>
                <p>Select a date to view slots</p>
              </div>
            )}

            {selectedDate && (
              <>
                <div className="bc-slots-header">
                  <h3>{selectedDate.toLocaleDateString("en-US", { weekday: "long", day: "2-digit", month: "long", year: "numeric" })}</h3>
                  {canManageBlocks && !slotsLoading && (
                    daySlots?.blocked ? (
                      <button type="button" className="bc-unblock-btn" onClick={() => handleUnblock(daySlots.blocked_id)}>
                        <i className="bi bi-unlock me-1"></i>Unblock Date
                      </button>
                    ) : (
                      <button type="button" className="bc-block-btn" onClick={() => openBlockModal(selectedDate)}>
                        <i className="bi bi-slash-circle me-1"></i>Block This Date
                      </button>
                    )
                  )}
                </div>

                {slotsLoading ? (
                  <div className="text-center py-5"><Loader text="Loading slots" size="30" /></div>
                ) : daySlots?.blocked ? (
                  <div className="bc-blocked-banner">
                    <i className="bi bi-slash-circle"></i>
                    <div>
                      <strong>This date is blocked.</strong>
                      {daySlots.blocked_reason && <p>{daySlots.blocked_reason}</p>}
                      <span className="text-muted">No bookings — app or admin — can be made on this date.</span>
                    </div>
                  </div>
                ) : daySlots?.rooms?.length ? (
                  daySlots.rooms.map((block) => (
                    <div className="bc-room-block" key={block.room._id}>
                      <div className="bc-room-block-header">
                        <span className={`badge ${block.room.is_icebox_room ? "badge-light-info" : "badge-light-secondary text-dark"}`}>
                          {block.room.is_icebox_room ? "Icebox" : "Standard"}
                        </span>
                        <h4>{block.room.title}</h4>
                      </div>
                      {block.slots.length === 0 ? (
                        <div className="bc-no-slots">Closed on this day.</div>
                      ) : (
                        <div className="bc-slot-grid">
                          {block.slots.map((slot) => (
                            <button
                              key={slot.start_time}
                              type="button"
                              className={`bc-slot-pill ${slot.is_booked ? "booked" : "available"}`}
                              onClick={() => slot.is_booked ? openBookedSlot(slot) : openSlotBooking(block.room, slot)}
                              title={slot.is_booked ? `${slot.booking?.customer_name || "Booked"} — ${slot.booking?.status || ""}` : (canManualCreate ? "Click to create a manual booking" : "Available")}
                            >
                              {slot.is_iced_slot && <i className="bi bi-snow2 bc-slot-ice"></i>}
                              <span className="bc-slot-time">{formatSlotRange(slot.start_time, slot.end_time)}</span>
                              <span className="bc-slot-status">
                                {slot.is_booked ? (slot.booking?.customer_name || "Booked") : "Available"}
                              </span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  ))
                ) : (
                  <div className="bc-no-slots">No rooms configured.</div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      <ManualBookingOffcanvas
        show={showManual}
        onClose={() => setShowManual(false)}
        rooms={rooms}
        prefill={manualPrefill}
        onCreated={refreshAfterChange}
      />

      <BookingDetailsOffcanvas
        bookingId={selectedBookingId}
        show={showBookingDetails}
        onClose={() => { setShowBookingDetails(false); setSelectedBookingId(null); }}
        onUpdate={refreshAfterChange}
      />

      {showBlockModal && (
        <>
          <div className="bc-modal-backdrop" onClick={() => setShowBlockModal(false)} />
          <div className="bc-modal">
            <div className="bc-modal-header">
              <h5>Block Dates</h5>
              <button type="button" className="btn-close" onClick={() => setShowBlockModal(false)} aria-label="Close"></button>
            </div>
            <div className="bc-modal-body">
              <p className="bc-modal-subtitle">
                Block a single date, or a range — no one, app users or admin, will be able to book any date in it.
              </p>
              <div className="row">
                <div className="col-6 mb-4">
                  <label className="required form-label">Start Date</label>
                  <input
                    type="date"
                    className="form-control form-control-solid"
                    value={blockRangeStart}
                    onChange={(e) => setBlockRangeStart(e.target.value)}
                  />
                </div>
                <div className="col-6 mb-4">
                  <label className="form-label">End Date</label>
                  <input
                    type="date"
                    className="form-control form-control-solid"
                    value={blockRangeEnd}
                    min={blockRangeStart || undefined}
                    onChange={(e) => setBlockRangeEnd(e.target.value)}
                  />
                  <div className="form-text">Leave blank to block a single date.</div>
                </div>
              </div>
              <div className="mb-2">
                <label className="form-label">Reason (optional)</label>
                <input
                  type="text"
                  className="form-control form-control-solid"
                  placeholder="e.g. Public holiday, Maintenance"
                  value={blockRangeReason}
                  onChange={(e) => setBlockRangeReason(e.target.value)}
                />
              </div>
            </div>
            <div className="bc-modal-footer">
              <button type="button" className="btn btn-light" onClick={() => setShowBlockModal(false)}>Cancel</button>
              <button type="button" className="btn btn-base" onClick={handleBlockRangeSubmit} disabled={blockRangeSubmitting}>
                {blockRangeSubmitting ? "Blocking…" : "Block Date(s)"}
              </button>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default BookingCalendarPage;
